import { useParams, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";

import {
  DetailTable,
  ExternalApprovalFlow,
  InvalidRoute,
  LoadingFrame,
} from "./ExternalApprovalFlow";
import {
  ComponentState,
  useComponentStateMachine,
} from "../../shared/hooks/useComponentStateMachine";
import { usePublicApiClient } from "../../hooks/usePublicApiClient";
import { DigitalColorElement } from "../../components/Logo/DigitalColorElement";
import { VectorColorElement } from "../../components/Logo/VectorColorElement";
import { ErrorView } from "../../components/error-view/ErrorView";
import { LogoImage } from "../../components/Logo/LogoImage";
import { mapToLogo } from "../../../data/models/mappers/mapLogo";
import {
  ApprovalType,
  BaseLogo,
  isDigital,
  LogoType,
  NameLogo,
  shouldShowApprovalButtons,
} from "../../../core/models/entities/BaseLogo";
import { MultiLogoApprovalCarousel } from "./MultiLogoApprovalCarousel";
import { JetSportBox } from "../../components/BoxView/JetSportBox";

export function ExternalApprovalLogoScreen({
  readOnly,
}: {
  readOnly?: boolean;
}) {
  const [qp] = useSearchParams();
  const params = useParams();
  const { getLogo, placeVerdictLogo, getMultipleLogos } = usePublicApiClient();
  const { dataTransition, state } = useComponentStateMachine();

  // Handle both single code and multiple keys scenarios
  const code = qp.get("code");
  const keys = qp.get("keys");
  const logoId = params?.logoId;

  // For multiple keys scenario
  if (keys) {
    const keyArray = keys.split(",");

    // Fetch multiple logos using the new API method
    const [logos, setLogos] = useState<BaseLogo[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedLogoIndex, setSelectedLogoIndex] = useState(0);

    useEffect(() => {
      getMultipleLogos(keyArray)
        .then((logoData) => {
          const mappedLogos = logoData.map(mapToLogo);
          setLogos(mappedLogos);
          setLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching multiple logos:", err);
          setError("Failed to load logos");
          setLoading(false);
        });
    }, [keys]);

    if (loading) return <LoadingFrame />;
    if (error) return <ErrorView />;
    if (logos.length === 0) return <div>No logos found</div>;

    const selectedLogo = logos[selectedLogoIndex];

    const handleLogoSelect = (logo: BaseLogo, index: number) => {
      setSelectedLogoIndex(index);
    };

    const handleVerdict = async (approval: boolean, note?: string) => {
      // For multi-logo approval, we need to handle verdict for the selected logo
      const publicKey = selectedLogo.approval?.publicKey;
      if (!publicKey) {
        console.error("No public key found for logo", selectedLogo.id);
        return;
      }

      try {
        await placeVerdictLogo(selectedLogo.id, approval, note, publicKey);
        // Refresh the logos data after successful verdict
        const updatedLogos = await getMultipleLogos(keyArray);
        const mappedUpdatedLogos = updatedLogos.map(mapToLogo);
        setLogos(mappedUpdatedLogos);
      } catch (error) {
        console.error(
          "Failed to place verdict for logo",
          selectedLogo.id,
          ":",
          error
        );
        throw error; // Re-throw to let ExternalApprovalFlow handle the error
      }
    };

    // Check if all logos are completed using new approval logic
    const allLogosCompleted = logos.every((logo) => {
      // A logo is completed if it doesn't need approval buttons (not pending for its type)
      return (
        !logo.approvalType ||
        !logo.approval ||
        !shouldShowApprovalButtons(logo.approval, logo.approvalType)
      );
    });

    return (
      <ExternalApprovalFlow
        subject="logo"
        title={selectedLogo.title}
        approvalType={selectedLogo.approvalType}
        approval={selectedLogo.approval}
        placeVerdict={handleVerdict}
        readOnly={readOnly}
        details={<LogoDetailTable logo={selectedLogo} />}
        carousel={
          <MultiLogoApprovalCarousel
            logos={logos}
            onSelectLogo={handleLogoSelect}
            selectedIndex={selectedLogoIndex}
            approvalType={selectedLogo.approvalType}
          />
        }
        view={
          <LogoImage
            className="min-h-[325px] min-w-[450px] max-h-[650px] max-w-[900px] object-scale-down bg-white rounded-bl"
            logo={selectedLogo}
            useHighRes={true}
          />
        }
        isMultiApproval={true}
        allItemsCompleted={allLogosCompleted}
        onViewItemsAgain={() => {
          // Reset to show logos again - this callback ensures we stay in approval view
        }}
      />
    );
  }

  // Original single logo logic
  if (!(code && logoId)) return <InvalidRoute />;

  const { isError, isLoading, isSuccess, data } = getLogo(+logoId, code);
  dataTransition(isLoading, isSuccess, isError);

  if (state === ComponentState.Failed) return <ErrorView />;
  if (state === ComponentState.Loading) return <LoadingFrame />;

  const logo = mapToLogo(data!);

  const handleVerdict = async (approval: boolean, note?: string) =>
    placeVerdictLogo(logo.id, approval, note, code);

  // Let ExternalApprovalFlow handle approval button visibility using shouldShowApprovalButtons

  return (
    <ExternalApprovalFlow
      subject="logo"
      title={logo.title}
      approvalType={logo.approvalType}
      approval={logo.approval}
      placeVerdict={handleVerdict}
      readOnly={readOnly}
      details={<LogoDetailTable logo={logo} />}
      view={
        <LogoImage
          className="min-h-[325px] min-w-[450px] max-h-[600px] max-w-[800px] object-scale-down bg-white rounded-bl"
          logo={logo}
          useHighRes={true}
        />
      }
    />
  );
}

interface LogoDetailTableProps {
  logo: BaseLogo;
}
export const LogoDetailTable = ({ logo }: LogoDetailTableProps) => (
  <div>
    <div className="font-bold mb-6">Details</div>
    <DetailTable
      fields={{
        ...(logo.productSku &&
          !logo.productSku.includes("UPLOAD_") && {
            "Item No": logo.productSku,
          }),
        Name: logo.title,
        Type:
          logo.transferMethod.type.toLowerCase() === "direk tryk" ||
          logo.transferMethod.type.toLowerCase() === "screen print"
            ? "Screen Print"
            : logo.transferMethod.type.toLowerCase() === "broderi" ||
              logo.transferMethod.type.toLowerCase() === "embroidery"
            ? "Embroidery"
            : logo.transferMethod.type
                .toLowerCase()
                .split(" ")
                .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                .join(" "),
        ...(logo.transferMethod.type.toLowerCase() !== "direk tryk" &&
          logo.transferMethod.type.toLowerCase() !== "screen print" &&
          logo.transferMethod.type.toLowerCase() !== "broderi" &&
          logo.transferMethod.type.toLowerCase() !== "embroidery" && {
            Method: logo.transferMethod.displayName
              .toLowerCase()
              .split(" ")
              .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
              .join(" "),
          }),
        Size: `${logo.widthMm}x${logo.heightMm}mm`,
        Colors: isDigital(logo) ? (
          <DigitalColorElement />
        ) : (
          logo.colorDetails.map((c, i) =>
            c.isIndigo ? (
              <DigitalColorElement key={i} showText={true} text={c.name} />
            ) : (
              <VectorColorElement key={i} color={c} />
            )
          )
        ),
        Font: (logo as NameLogo).font,
      }}
    />
    {logo.logoType === LogoType.NameLogo && <NameLogoNote />}
    {logo.logoType === LogoType.NumberLogo && <NumberLogoNote />}
  </div>
);

interface LogoNoteProps {
  text: string;
}
const LogoNote = ({ text }: LogoNoteProps) => (
  <div className="max-w-[325px]">
    <strong>Note: </strong>
    {text}
  </div>
);
const NameLogoNote = () => (
  <LogoNote text='This is a dynamic print. The displayed "Name" is just an example - the final print will feature your chosen names using the selected font.' />
);
const NumberLogoNote = () => (
  <LogoNote text="This is a dynamic print. The displayed number of just an example - the final print will feature your chosen numbers." />
);
